import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { getVisitorAthletes } from 'api/visitorsApi';

import MetaTags from 'components/common/MetaTags';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import PinProtectedAction from 'components/common/PinProtected/PinProtectedAction';
import AthleteActionButtons from 'components/admin/AthleteActionButtons/AthleteActionButtons';
import RosterRemovePreviewModal from 'components/admin/RosterRemovePreviewModal';

import useBulkSelection from 'hooks/useBulkSelection';
import useTableSort from 'hooks/useTableSort';
import useSessionFilters from 'hooks/useSessionFilters';
import { formatExpiresRelative } from 'utils/time';
import {
  activeAgeFilter,
  ageFilterOptions,
  matchesAgeFilter,
} from 'utils/ageFilter';
import BulkActionBar from 'components/admin/BulkActionBar';
import MultiSelectFilter from 'components/admin/MultiSelectFilter';

import styles from 'components/admin/bulkAdminTable.module.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'YES', label: 'Active' },
  { value: 'NO', label: 'Expired' },
];

const GENDER_OPTIONS = [
  { value: 'all', label: 'All genders' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

// Sentinel for the "no representing club" (individual visitor) bucket in the Club filter.
const INDIVIDUAL_VALUE = '__individual__';

const VisitorAthletes = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const clubId = selectedClub?.clubID || '';

  const [loading, setLoading] = useState(true);
  const [athletes, setAthletes] = useState([]);

  // Filters — persisted in sessionStorage so they survive navigate-away-and-back.
  const [filters, setFilter] = useSessionFilters(
    `visitorAthletesFilters_${clubId}`,
    {
      searchText: '',
      age: [],
      gender: 'all',
      club: 'all',
      meet: 'all',
      status: 'all',
    }
  );
  const {
    searchText,
    gender: genderFilter,
    club: clubFilter,
    meet: meetFilter,
    status: statusFilter,
  } = filters;

  // Bulk remove — change-preview model (§5.6): preview the cascade, confirm, apply.
  const [removeSeed, setRemoveSeed] = useState(null);

  const fetchAthletes = useCallback(async () => {
    if (!clubId) return;
    try {
      setLoading(true);
      const response = await getVisitorAthletes(clubId);
      const enriched = (response.data || []).map((a) => ({
        ...a,
        athleteName: `${a.firstName || ''} ${a.lastName || ''}`.trim(),
        meetNames: (a.meets || []).map((x) => x.meetName).join(', '),
        // API returns participationStatus in mixed case ("Yes"/"No"); normalise so
        // filter values and badge logic compare against a single canonical form.
        participationStatus: (a.participationStatus || '').toUpperCase(),
      }));
      setAthletes(enriched);
    } catch (err) {
      console.error('[VisitorAthletes] fetch error:', err);
      toast.error('Failed to load visitor athletes');
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    fetchAthletes();
  }, [fetchAthletes]);

  // Age filter options derived from the loaded data (mirrors Guest Athletes;
  // "All ages" is the empty selection of the multi-select, not an option).
  const ageOptions = useMemo(
    () =>
      ageFilterOptions(
        athletes.map((a) => a.ageGroup),
        selectedClub
      ),
    [athletes, selectedClub]
  );

  // Ticked age values (empty = all ages), OR'd together — see utils/ageFilter.
  const ageFilter = useMemo(
    () => activeAgeFilter(filters.age, ageOptions),
    [filters.age, ageOptions]
  );

  // Club dropdown options derived from the data: distinct representing clubs plus an "Individual" bucket.
  const clubOptions = useMemo(() => {
    const distinctClubs = Array.from(
      new Set(
        athletes
          .map((a) => (a.externalClubName || '').trim())
          .filter((s) => s !== '')
      )
    ).sort((a, b) => a.localeCompare(b));
    const hasIndividual = athletes.some(
      (a) => !a.externalClubName || a.externalClubName.trim() === ''
    );

    const opts = [{ value: 'all', label: 'All clubs' }];
    if (hasIndividual) {
      opts.push({ value: INDIVIDUAL_VALUE, label: 'Individual' });
    }
    distinctClubs.forEach((c) => opts.push({ value: c, label: c }));
    return opts;
  }, [athletes]);

  // Meet dropdown options derived from the data: distinct meets, most recent first.
  const meetOptions = useMemo(() => {
    const byId = new Map();
    athletes.forEach((a) =>
      (a.meets || []).forEach((meet) => {
        if (!byId.has(meet.meetID)) byId.set(meet.meetID, meet);
      })
    );
    const opts = [{ value: 'all', label: 'All meets' }];
    Array.from(byId.values())
      .sort((a, b) => new Date(b.meetDate) - new Date(a.meetDate))
      .forEach((meet) => opts.push({ value: meet.meetID, label: meet.meetName }));
    return opts;
  }, [athletes]);

  // Filters
  const filteredAthletes = useMemo(() => {
    return athletes.filter((a) => {
      if (!matchesAgeFilter(a.ageGroup, ageFilter)) return false;
      if (clubFilter !== 'all') {
        const ext = (a.externalClubName || '').trim();
        if (clubFilter === INDIVIDUAL_VALUE) {
          if (ext !== '') return false;
        } else if (ext !== clubFilter) {
          return false;
        }
      }
      if (meetFilter !== 'all' && !(a.meets || []).some((x) => x.meetID === meetFilter)) return false;
      if (statusFilter !== 'all' && a.participationStatus !== statusFilter) return false;
      if (genderFilter !== 'all' && a.gender !== genderFilter) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        const hay = `${a.athleteName} ${a.externalClubName || ''} ${a.meetNames || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [athletes, ageFilter, clubFilter, meetFilter, statusFilter, genderFilter, searchText]);

  // Sort
  const sortAccessors = useMemo(
    () => ({
      clubAthleteNumber: (r) => r.clubAthleteNumber || 0,
      athleteName: (r) => `${(r.lastName || '').trim()} ${(r.firstName || '').trim()}`.toLowerCase(),
      ageGroup: (r) => r.ageGroup,
      gender: (r) => r.gender,
      externalClubName: (r) => r.externalClubName,
      meetNames: (r) => r.meetNames,
      participationStatus: (r) => r.participationStatus,
      expiresAt: (r) => r.expiresAt,
    }),
    []
  );
  const { sortedItems, sortKey, sortDir, setSort } = useTableSort(filteredAthletes, {
    defaultKey: 'athleteName',
    accessors: sortAccessors,
  });

  // Selection
  const idAccessor = useCallback((a) => a.athleteID, []);
  const selection = useBulkSelection(sortedItems, idAccessor);

  const sortIndicator = (key) =>
    sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '';

  return (
    <>
      <MetaTags title="SplitFast | Visitor Athletes" />
      <AdminTablePageLayout loading={loading}>
        <PinProtectedAction clubId={clubId}>
          {({ requestPinValidation }) => {
            const triggerWithPin = (actionType, onApproved, description) =>
              requestPinValidation(actionType, () => onApproved(), null, description);

            const bulkActions = [
              {
                label: 'Remove',
                variant: 'danger',
                title: 'Remove from this club (athlete profile and historical results are preserved)',
                onClick: () =>
                  triggerWithPin(
                    'bulk-remove-visitor-athletes',
                    () => setRemoveSeed({ athleteIds: selection.selectedIds }),
                    "The Admin PIN is required to remove visitor athletes from this club. The athletes' race results history is not deleted. You will see exactly what will be removed — including future meet registrations and now-empty visitor contacts — before confirming."
                  ),
              },
            ];

            return (
              <div className={styles.container}>
                <div className={styles.pageHeading}>
                  <div className={styles.pageTitle}>
                    Visitor Athletes
                    <span className={styles.countBadge}>
                      {athletes.length} {athletes.length === 1 ? 'Athlete' : 'Athletes'}
                    </span>
                  </div>
                </div>

                {/* Filter card */}
                <div className={styles.filterCard}>
                  <div className={`${styles.filterGroup} ${styles.filterGroupSearch}`}>
                    <label className={styles.filterLabel}>Search</label>
                    <input
                      className={styles.filterInput}
                      type="search"
                      placeholder="Search by athlete name, club, or meet…"
                      value={searchText}
                      onChange={(e) => setFilter('searchText', e.target.value)}
                    />
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Age</label>
                    <MultiSelectFilter
                      value={ageFilter}
                      options={ageOptions}
                      onChange={(next) => setFilter('age', next)}
                      allLabel="All ages"
                      itemLabel="age"
                    />
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Gender</label>
                    <select
                      className={styles.filterSelect}
                      value={genderFilter}
                      onChange={(e) => setFilter('gender', e.target.value)}
                    >
                      {GENDER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Club</label>
                    <select
                      className={styles.filterSelect}
                      value={clubFilter}
                      onChange={(e) => setFilter('club', e.target.value)}
                    >
                      {clubOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Meet</label>
                    <select
                      className={styles.filterSelect}
                      value={meetFilter}
                      onChange={(e) => setFilter('meet', e.target.value)}
                    >
                      {meetOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Status</label>
                    <select
                      className={styles.filterSelect}
                      value={statusFilter}
                      onChange={(e) => setFilter('status', e.target.value)}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.filterResultCount}>
                    Showing <strong>{filteredAthletes.length}</strong> of {athletes.length}
                  </div>
                </div>

                <BulkActionBar
                  count={selection.count}
                  itemLabel="athlete"
                  actions={bulkActions}
                  onClear={selection.clear}
                  allSelected={selection.allSelected}
                  someSelected={selection.someSelected}
                  onToggleAll={selection.toggleAll}
                />

                <div className={styles.tableCard}>
                  <div className={styles.tableWrapper}>
                    <table className={styles.dataTable}>
                      <colgroup>
                        <col style={{ width: '44px' }} />
                        <col style={{ width: '90px' }} />
                        <col />
                        <col style={{ width: '70px' }} />
                        <col style={{ width: '80px' }} />
                        <col style={{ width: '160px' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '110px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '180px' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th
                            className={`${styles.checkboxCell} ${styles.notSortable}`}
                            aria-label="Select"
                          />

                          <th onClick={() => setSort('clubAthleteNumber')}>
                            ID <span className={styles.sortIndicator}>{sortIndicator('clubAthleteNumber')}</span>
                          </th>
                          <th onClick={() => setSort('athleteName')}>
                            Name <span className={styles.sortIndicator}>{sortIndicator('athleteName')}</span>
                          </th>
                          <th onClick={() => setSort('ageGroup')}>
                            Age <span className={styles.sortIndicator}>{sortIndicator('ageGroup')}</span>
                          </th>
                          <th onClick={() => setSort('gender')}>
                            Gender <span className={styles.sortIndicator}>{sortIndicator('gender')}</span>
                          </th>
                          <th onClick={() => setSort('externalClubName')}>
                            Club <span className={styles.sortIndicator}>{sortIndicator('externalClubName')}</span>
                          </th>
                          <th onClick={() => setSort('meetNames')}>
                            Meets <span className={styles.sortIndicator}>{sortIndicator('meetNames')}</span>
                          </th>
                          <th onClick={() => setSort('participationStatus')}>
                            Status <span className={styles.sortIndicator}>{sortIndicator('participationStatus')}</span>
                          </th>
                          <th onClick={() => setSort('expiresAt')}>
                            Expires <span className={styles.sortIndicator}>{sortIndicator('expiresAt')}</span>
                          </th>
                          <th className={styles.notSortable}>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedItems.length === 0 ? (
                          <tr className={styles.emptyRow}>
                            <td colSpan={10}>
                              {athletes.length === 0
                                ? 'No visitor athletes in this club.'
                                : 'No athletes match the current filters.'}
                            </td>
                          </tr>
                        ) : (
                          sortedItems.map((a) => {
                            const checked = selection.isSelected(a.athleteID);
                            return (
                              <tr
                                key={a.athleteID}
                                className={checked ? styles.rowSelected : ''}
                              >
                                <td className={styles.checkboxCell}>
                                  <input
                                    type="checkbox"
                                    aria-label={`Select ${a.athleteName}`}
                                    checked={checked}
                                    onChange={() => selection.toggle(a.athleteID)}
                                  />
                                </td>
                                <td>{a.clubAthleteNumber || 0}</td>
                                <td>{a.athleteName}</td>
                                <td>{a.ageGroup}</td>
                                <td>{a.gender}</td>
                                <td>
                                  {a.externalClubName || (
                                    <span className={styles.muted}>—</span>
                                  )}
                                </td>
                                <td>
                                  {(a.meets || []).length > 0 ? (
                                    <div className={styles.pillList}>
                                      {a.meets.map((x) => (
                                        <span key={x.meetID} className={styles.pill}>
                                          {x.meetName}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className={styles.muted}>—</span>
                                  )}
                                </td>
                                <td>
                                  <span
                                    className={`${styles.badge} ${a.participationStatus === 'YES' ? styles.badgeYes : styles.badgeNo}`}
                                  >
                                    {a.participationStatus === 'YES' ? 'Active' : 'Expired'}
                                  </span>
                                </td>
                                <td>{formatExpiresRelative(a.expiresAt)}</td>
                                <td>
                                  <AthleteActionButtons
                                    clubId={clubId}
                                    athleteId={a.athleteID}
                                  />
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          }}
        </PinProtectedAction>

        {/* Bulk remove — change-preview (§5.6): preview the cascade, confirm, apply */}
        <RosterRemovePreviewModal
          show={!!removeSeed}
          clubId={clubId}
          seed={removeSeed}
          title="Remove Visitor Athletes"
          typeLabel="visitor"
          onClose={() => setRemoveSeed(null)}
          onApplied={() => {
            selection.clear();
            fetchAthletes();
          }}
        />
      </AdminTablePageLayout>
    </>
  );
};

export default VisitorAthletes;
